/**
 * BE#498 — FE mirror of the backend cancellation taxonomy + save-offer mapping.
 * Kept in lockstep with whatsapp-scheduler-backend/src/services/actions/actions/
 * cancellation-taxonomy.ts. The backend re-normalises anything the FE sends
 * (unknown → 'other'), so the FE is presentation-only; it only holds the cancel when the
 * server says CANCEL_REASON_REQUIRED is on (#241).
 */

// A TYPE-only import of i18next's own `TFunction`: this module stays pure data
// (no react-i18next, no React runtime) while the parameter is the real thing.
import type { TFunction } from 'i18next';

/**
 * The customer chose Skip on the reason step (or continued without picking one). Accepted by the
 * server whether or not CANCEL_REASON_REQUIRED is on; stored as itself; never sent to Chargebee.
 * It is not one of the survey options in `cancelReasons(t)`.
 */
export const SKIPPED_REASON_CODE = 'skipped' as const;

export type CancelReasonCode =
  | 'too_expensive'
  | 'missing_feature'
  | 'not_using_enough'
  | 'deliverability_issues'
  | 'technical_problems'
  | 'switched_competitor'
  | 'no_longer_needed'
  | 'other'
  | typeof SKIPPED_REASON_CODE;

// #241: 'annual' = switch to the yearly plan at 15% off the first year (server-quoted).
export type SaveOfferType = 'pause' | 'discount' | 'downgrade' | 'support' | 'annual' | 'none';

export type SaveOfferOutcome = 'accepted' | 'declined' | 'not_shown';

/** One row of the cancellation survey. */
export interface CancelReasonOption {
  code: CancelReasonCode;
  label: string;
}

/**
 * The reasons shown in the survey, in display order, in the reader's language.
 *
 * This was a module-level array of English labels evaluated at import time, where
 * no `t` exists — so a Hebrew customer answering "why are you leaving?" read eight
 * English radio labels at the one moment we most need a truthful answer. Same shape
 * and same fix as `campaignSortLabel` / `collectAudienceOptions`: `t` is a REQUIRED
 * parameter, so the compiler finds every call site rather than leaving a forgotten
 * one silently rendering English.
 *
 * The CODES are the wire contract with the backend taxonomy and never move; only
 * the labels are translated.
 */
export function cancelReasons(t: TFunction): CancelReasonOption[] {
  return [
    { code: 'too_expensive', label: t('cancelFlow.reasons.tooExpensive', "It's too expensive") },
    { code: 'not_using_enough', label: t('cancelFlow.reasons.notUsingEnough', "I'm not using it enough right now") },
    { code: 'missing_feature', label: t('cancelFlow.reasons.missingFeature', "It's missing a feature I need") },
    { code: 'deliverability_issues', label: t('cancelFlow.reasons.deliverabilityIssues', 'My messages had delivery problems') },
    { code: 'technical_problems', label: t('cancelFlow.reasons.technicalProblems', 'I ran into technical problems') },
    { code: 'switched_competitor', label: t('cancelFlow.reasons.switchedCompetitor', 'I switched to another tool') },
    { code: 'no_longer_needed', label: t('cancelFlow.reasons.noLongerNeeded', 'I no longer need WhatsApp automation') },
    { code: 'other', label: t('cancelFlow.reasons.other', 'Something else') },
  ];
}

/** Reason → the single save offer surfaced (mirrors the backend REASON_TO_OFFER map). */
export const REASON_TO_OFFER: Record<CancelReasonCode, SaveOfferType> = {
  not_using_enough: 'pause',
  no_longer_needed: 'pause',
  too_expensive: 'discount',
  missing_feature: 'support',
  deliverability_issues: 'support',
  technical_problems: 'support',
  switched_competitor: 'none',
  other: 'none',
  skipped: 'none',
};

export function recommendedOfferFor(code: CancelReasonCode): SaveOfferType {
  return REASON_TO_OFFER[code] ?? 'none';
}

/** #241 — the server's annual-save quote for this customer (all money in cents). */
export interface AnnualSaveQuote {
  subscriptionId: string;
  monthlyItemPriceId: string;
  yearlyItemPriceId: string;
  quantity: number;
  currency: string;
  pct: number;
  currentMonthlyCents: number;
  listYearlyCents: number;
  offerYearlyCents: number;
}

/** #241 — `chargebee.getCancelFlowConfig`. */
export interface CancelFlowConfig {
  cancelReasonRequired?: boolean;
  /** offers one flow may show (CANCEL_FLOW_MAX_OFFERS, default 1 = California one-offer rule) */
  maxOffers?: number;
  /** present only when CANCEL_ANNUAL_SAVE_ENABLED is on and this customer is eligible */
  annualSave?: AnnualSaveQuote | null;
}

/**
 * #241 — the offers to show, best first, capped at the server's `maxOffers` (default 1).
 * A price objection with an annual quote gets the annual plan first, the discount second,
 * so with one offer allowed the annual REPLACES the discount.
 */
export function saveOffersFor(code: CancelReasonCode | '', config?: CancelFlowConfig): SaveOfferType[] {
  if (!code) return [];
  const offers: SaveOfferType[] = code === 'too_expensive' && config?.annualSave
    ? ['annual', 'discount']
    : [recommendedOfferFor(code)];
  const max = Math.max(1, Math.floor(config?.maxOffers ?? 1));
  return offers.filter((o) => o !== 'none').slice(0, max);
}

/**
 * #1182 — a trial (status `in_trial`) has no paid term: the survey is still asked (we want the why),
 * but there is nothing to discount, pause or move to annual, so no save offer is shown and the
 * cancel ends access now.
 */
/**
 * #1182 — mirrors the backend rule (isImmediateTrialCancel): a trial ends NOW on cancel only when it is
 * `in_trial` AND set to cancel at trial end (our no-card trials). Any other trial keeps the end-of-term cancel.
 */
export function endsAccessNowOnCancel(subscription: { status?: string; trial_end_action?: string } | undefined): boolean {
  return subscription?.status === 'in_trial' && subscription.trial_end_action === 'cancel_subscription';
}

export function offersForSubscription(code: CancelReasonCode | '', config: CancelFlowConfig | undefined, isTrial: boolean): SaveOfferType[] {
  return isTrial ? [] : saveOffersFor(code, config);
}

/** Cents → "$540" / "$45.90" in the reader's locale. */
export function formatPrice(cents: number, currency: string, locale?: string): string {
  const amount = cents / 100;
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency || 'USD',
    minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/** Researched constants (issue #498 Resolved decisions). */
export const SAVE_OFFER_DISCOUNT_PCT = 40; // capped: 40% ≈ 50% on save-rate
export const PAUSE_OPTIONS_MONTHS: number[] = [1, 3];
export const DEFAULT_PAUSE_MONTHS = 1;

/** The survey payload the FE sends to chargebee.cancelActiveSubscription. */
export interface CancellationSurveyPayload {
  reasonCode?: CancelReasonCode;
  reasonDetail?: string;
  offerShown?: SaveOfferType;
  offerOutcome?: SaveOfferOutcome;
}

/** #241 — the server's refusal when CANCEL_REASON_REQUIRED is on and the reason is missing. */
export interface CancelReasonError {
  code: 'cancel_reason_required' | 'cancel_reason_detail_required';
  field: 'reasonCode' | 'reasonDetail';
}

/** Mirrors the backend's stored-length cap on the free-text reason. */
export const REASON_DETAIL_MAX_LENGTH = 2000;

/** Pull a CancelReasonError out of an RTK Query error (Feathers BadRequest body), else null. */
export function cancelReasonErrorOf(error: unknown): CancelReasonError | null {
  const data = (error as { data?: { data?: { code?: unknown; field?: unknown } } } | undefined)?.data?.data;
  if (data?.code === 'cancel_reason_required') return { code: data.code, field: 'reasonCode' };
  if (data?.code === 'cancel_reason_detail_required') return { code: data.code, field: 'reasonDetail' };
  return null;
}

/**
 * May the survey step continue? Continue needs a picked reason. Skip is a separate, equal-weight
 * button that records `skipped` and moves on, so it never reaches this gate. An explicit skip still
 * completes the step. "Something else" also needs non-blank text when the reason is required (#241).
 */
export function isSurveyComplete(reasonCode: CancelReasonCode | '', reasonDetail: string, reasonRequired: boolean): boolean {
  if (reasonCode === SKIPPED_REASON_CODE) return true;
  if (!reasonCode) return false;
  return !(reasonRequired && reasonCode === 'other' && !reasonDetail.trim());
}
